'use client'

import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { RiCheckboxMultipleBlankLine } from 'react-icons/ri'

import { usePathname } from 'next/navigation'

import { setCookie } from 'nookies'

import {
  DashboardIcon,
  GroupsAwardedOutlineRoundedIcon,
  GroupsOutlineRoundedIcon,
  ShoppingCartIcon,
  CardsIcon,
} from '@/components/icons'
import { cookiesOLP } from '@/cookies/constants'
import { routerPaths } from '@/utils/constants/routes'

import { OrdersContext } from './SidebarContext'
import {
  IMenuItems,
  ISidebarContextProps,
  ISidebarProviderProps,
} from './types'

export const SidebarProvider: FC<ISidebarProviderProps> = ({
  children,
  sidebarCookies,
}) => {
  const routerPathName = usePathname()

  const [activeMenu, setActiveMenu] = useState<string | null>(
    sidebarCookies?.activeMenu,
  )
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(
    sidebarCookies.activeSubMenu,
  )
  const [isSubmenuOpen, setIsSubmenuOpen] = useState<boolean>(
    sidebarCookies.isSubmenuOpen,
  )

  const menuItems: IMenuItems = useMemo(
    () => [
      {
        icon: <DashboardIcon />,
        label: 'Início',
        rootPath: routerPaths.private.dashboard.pathName,
        pathName: routerPaths.private.dashboard.pathName,
        path: routerPaths.private.dashboard.default,
        subMenu: null,
      },
      {
        icon: <ShoppingCartIcon />,
        label: 'Pedidos',
        rootPath: routerPaths.private.dashboard.orders.pathName,
        pathName: routerPaths.private.dashboard.orders.pathName,
        path: routerPaths.private.dashboard.orders.default,
        subMenu: null,
      },
      {
        icon: <RiCheckboxMultipleBlankLine />,
        label: 'Ações em Lote',
        rootPath: routerPaths.private.dashboard.batchActions.pathName,
        pathName: routerPaths.private.dashboard.batchActions.pathName,
        path: routerPaths.private.dashboard.batchActions.default,
        subMenu: null,
      },
      {
        icon: <GroupsAwardedOutlineRoundedIcon />,
        label: 'Premiados',
        rootPath: routerPaths.private.dashboard.awarded.pathName,
        pathName: routerPaths.private.dashboard.awarded.pathName,
        path: routerPaths.private.dashboard.awarded.default,
        subMenu: null,
      },
      {
        icon: <CardsIcon />,
        label: 'Cartões',
        rootPath: routerPaths.private.dashboard.cards.pathName,
        pathName: routerPaths.private.dashboard.cards.pathName,
        path: routerPaths.private.dashboard.cards.default,
        subMenu: [
          {
            label: 'Consulta',
            rootPath: routerPaths.private.dashboard.cards.pathName,
            pathName: routerPaths.private.dashboard.cards.consult().pathName,
            path: routerPaths.private.dashboard.cards.consult().default,
          },
          {
            label: 'Estoque',
            rootPath: routerPaths.private.dashboard.cards.pathName,
            pathName: routerPaths.private.dashboard.cards.stock().pathName,
            path: routerPaths.private.dashboard.cards.stock().default,
          },
        ],
      },
      {
        icon: <GroupsOutlineRoundedIcon />,
        label: 'Usuários',
        rootPath: routerPaths.private.dashboard.users.pathName,
        pathName: routerPaths.private.dashboard.users.pathName,
        path: routerPaths.private.dashboard.users.default,
        subMenu: null,
      },
    ],
    [],
  )

  const handleSetCookieSidebar = useCallback(() => {
    const dataAtual = new Date()

    setCookie(
      null,
      cookiesOLP.olp_sidebar,
      JSON.stringify({ activeMenu, activeSubMenu, isSubmenuOpen }),
      {
        path: '/',
        maxAge: dataAtual.setTime(dataAtual.getTime() + 60 * 1000),
        secure: true,
      },
    )
  }, [activeMenu, activeSubMenu, isSubmenuOpen])

  const checksCurrentRoute = useCallback(() => {
    if (menuItems.some((i) => i.path === routerPathName)) {
      // Verifica se tem submenu aberto porém nenhum está setado
      if (isSubmenuOpen && !activeSubMenu?.length) {
        // Encontra a rota no primeiro nível de menuItems
        const menuItemFound = menuItems.find(
          (menuItem) => menuItem.path === routerPathName,
        )

        // Verifica se encontrou o menu
        if (menuItemFound) {
          setActiveMenu(menuItemFound.pathName)
        }
      }

      // Verifica se tem submenu aberto e se está setado
      if (isSubmenuOpen && activeSubMenu?.length) {
        // Encontra o pathName no segundo nível de menuItems
        const submenuFound = menuItems
          .flatMap((menuItem) => menuItem?.subMenu)
          .find((submenuItem) => submenuItem?.pathName === activeSubMenu)

        // Verifica se encontrou o submenu
        if (submenuFound) {
          // Verifica se o rootPath do submenu é diferente do activeMenu
          if (submenuFound.rootPath !== activeMenu) {
            // Seta a rota raiz de submenu que está selecionado no momento atual
            setActiveMenu(submenuFound.rootPath)
          }
        }
      }
    }

    if (
      !menuItems.some((i) => i.path === routerPathName) &&
      activeSubMenu?.length
    ) {
      if (activeMenu?.length && isSubmenuOpen) {
        const submenuFound = menuItems
          .flatMap((menuItem) => menuItem?.subMenu)
          .find((submenuItem) => submenuItem?.pathName === activeSubMenu)

        // Verifica se encontrou o submenu
        if (submenuFound) {
          // Verifica se o rootPath do submenu é diferente do activeMenu
          if (submenuFound.rootPath !== activeMenu) {
            // Seta a rota raiz de submenu que está selecionado no momento atual
            setActiveMenu(submenuFound.rootPath)
          }
        }
      }

      return
    }
  }, [activeMenu, activeSubMenu, isSubmenuOpen, menuItems, routerPathName])

  const checkCookieRegistration = useCallback(() => {
    // Verifica se não tem registro nos cookies
    if (!isSubmenuOpen && !activeMenu?.length) {
      // Encontra a rota no primeiro nível de menuItems
      if (menuItems.some((i) => i.path === routerPathName)) {
        const menuFound = menuItems?.find(
          (menu) => menu.path === routerPathName,
        )

        // Verifica se encontrou o menu
        if (menuFound) {
          // setActiveMenu(menuFound?.pathName)
        }
      }

      // Caso não encontre no primeiro nível de menuItems, percorre as listas de submenu até encontra a rota atual
      const submenuFound = menuItems
        .flatMap((menuItem) => menuItem?.subMenu)
        .find((submenuItem) => submenuItem?.path === routerPathName)

      // Verifica se encontrou o submenu
      if (submenuFound) {
        // Seta a rota raiz
        // setActiveMenu(submenuFound.rootPath)
        // Seta a rota do submenu
        setActiveSubMenu(submenuFound.pathName)
        // Exibe o submenu
        setIsSubmenuOpen(true)
      }
    }
  }, [activeMenu?.length, isSubmenuOpen, menuItems, routerPathName])

  const checkInitialRoute = useCallback(() => {
    const menuToCurrentPathname = menuItems.find(
      (menuItem) => menuItem?.path === routerPathName,
    )

    const submenuToCurrentPathname = menuItems
      .flatMap((menuItem) => menuItem?.subMenu)
      .find((submenuItem) => submenuItem?.path === routerPathName)

    // se o submenu está aberto
    if (isSubmenuOpen) {
      if (submenuToCurrentPathname) {
        // se o pathName é diferente activeSubMenu
        if (submenuToCurrentPathname.pathName !== activeSubMenu) {
          // setActiveMenu(submenuToCurrentPathname?.rootPath)
          setActiveSubMenu(submenuToCurrentPathname?.pathName)
        }
      }
      // Diz que o submenu está aberto mas não encontrou a rota nos submenus
      if (!submenuToCurrentPathname) {
        if (menuToCurrentPathname) {
          // setActiveMenu(menuToCurrentPathname.pathName)
          setActiveSubMenu(null)
          setIsSubmenuOpen(false)
        }
      }
    }
    // se o submenu está fechado
    if (!isSubmenuOpen) {
      if (menuToCurrentPathname) {
        // se pathname do menu não é igual ao activeMenu e se existe submenu
        if (
          menuToCurrentPathname.pathName !== activeMenu &&
          menuToCurrentPathname.subMenu?.length
        ) {
          // Não é igual e tem submenu
          if (submenuToCurrentPathname) {
            // setActiveMenu(submenuToCurrentPathname?.rootPath)
            setActiveSubMenu(submenuToCurrentPathname?.pathName)
            setIsSubmenuOpen(true)
          }
        }
        // se pathname do menu não é igual ao activeMenu e se não existe submenu
        if (
          menuToCurrentPathname.pathName !== activeMenu &&
          !menuToCurrentPathname.subMenu?.length
        ) {
          // Não é igual e não tem submenu
          // setActiveMenu(menuToCurrentPathname.pathName)
        }
      }

      // se não existe menuToCurrentPathname
      if (!menuToCurrentPathname) {
        // Diz que o submenu está fechado mas não existe a rota no primeiro nivel de menuItems
        if (submenuToCurrentPathname) {
          // setActiveMenu(submenuToCurrentPathname.rootPath)
          setActiveSubMenu(submenuToCurrentPathname.pathName)
          setIsSubmenuOpen(true)
        }
      }
    }
  }, [activeMenu, activeSubMenu, isSubmenuOpen, menuItems, routerPathName])

  useEffect(() => {
    // Verifica os cookies gravados para nunca deixar de preencher o menu e submenu selecionado
    checkCookieRegistration()
    // Seta nos cookies os atuais valores do menu e submenu
    handleSetCookieSidebar()
  }, [checkCookieRegistration, handleSetCookieSidebar])

  useEffect(() => {
    // Ao iniciar ou recarregar a página, seta os primeiros valores do menu e submenu
    checkInitialRoute()
    // desabilita o eslint pois o esperado é executar apenas no carregamento
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue: ISidebarContextProps = useMemo(() => {
    return {
      menuItems,
      activeMenu,
      setActiveMenu,
      activeSubMenu,
      setActiveSubMenu,
      isSubmenuOpen,
      setIsSubmenuOpen,
      checksCurrentRoute,
    }
  }, [activeMenu, activeSubMenu, checksCurrentRoute, isSubmenuOpen, menuItems])

  return (
    <OrdersContext.Provider value={contextValue}>
      {children}
    </OrdersContext.Provider>
  )
}

export const useSidebarProvider = () => useContext(OrdersContext)
